import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getPendingWorks, confirmWork, confirmAllWorks, rejectWork, authRegister } from '../services/api';
import { PendingWork, PendingWorksResponse } from '../types';
import { useToast } from './Toast';

type PageState = 'loading' | 'invalid' | 'error' | 'ready' | 'completed';

const PendingWorksView: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [pageState, setPageState] = useState<PageState>('loading');
  const [data, setData] = useState<PendingWorksResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consent, setConsent] = useState(false);
  const [selectedWorks, setSelectedWorks] = useState<Set<string>>(new Set());
  const [confirmedWorks, setConfirmedWorks] = useState<Set<string>>(new Set());
  const [rejectedWorks, setRejectedWorks] = useState<Set<string>>(new Set());
  const [rejectingWorkId, setRejectingWorkId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const { showToast, ToastComponent } = useToast();

  // Signup form state
  const [showSignupForm, setShowSignupForm] = useState(false);
  const [signupName, setSignupName] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupConfirmPassword, setSignupConfirmPassword] = useState('');
  const [isSigningUp, setIsSigningUp] = useState(false);

  useEffect(() => {
    const fetchPendingWorks = async () => {
      if (!token) {
        setPageState('invalid');
        setError('Invalid link');
        return;
      }

      try {
        const response = await getPendingWorks(token);
        setData(response);
        
        // Pre-select all pending works
        const pendingIds = response.works
          .filter(w => w.status === 'pending')
          .map(w => w.id);
        setSelectedWorks(new Set(pendingIds));
        
        // Track already confirmed works
        const confirmedIds = response.works
          .filter(w => w.status === 'confirmed')
          .map(w => w.id);
        setConfirmedWorks(new Set(confirmedIds));
        
        // Track already rejected works
        const rejectedIds = response.works
          .filter(w => w.status === 'rejected')
          .map(w => w.id);
        setRejectedWorks(new Set(rejectedIds));
        
        setPageState('ready');
      } catch (err) {
        if (err instanceof Error && err.message === 'INVALID_TOKEN') {
          setPageState('invalid');
          setError('This link is invalid or has expired. Please contact the person who invited you to request a new link.');
        } else {
          setPageState('error');
          setError(err instanceof Error ? err.message : 'An unexpected error occurred');
        }
      }
    };

    fetchPendingWorks();
  }, [token]);

  const toggleWorkSelection = (workId: string) => {
    const newSelected = new Set(selectedWorks);
    if (newSelected.has(workId)) {
      newSelected.delete(workId);
    } else {
      newSelected.add(workId);
    }
    setSelectedWorks(newSelected);
  };

  const selectAllPending = () => {
    const pendingIds = data?.works
      .filter(w => w.status === 'pending' && !confirmedWorks.has(w.id) && !rejectedWorks.has(w.id))
      .map(w => w.id) || [];
    setSelectedWorks(new Set(pendingIds));
  };

  const deselectAll = () => {
    setSelectedWorks(new Set());
  };

  const handleConfirmSelected = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !data || selectedWorks.size === 0) return;

    setIsSubmitting(true);

    try {
      if (selectedWorks.size === data.works.filter(w => w.status === 'pending').length) {
        // Confirm all at once
        const result = await confirmAllWorks(token, {
          typedName: data.email,
          consent
        });
        
        showToast(`Successfully confirmed ${result.confirmed} split${result.confirmed > 1 ? 's' : ''}!`, 'success');
        
        // Update local state
        const mergedConfirmed = new Set(Array.from(confirmedWorks).concat(Array.from(selectedWorks)));
        setConfirmedWorks(mergedConfirmed);
        setSelectedWorks(new Set());
        
        // Refresh data
        const refreshedData = await getPendingWorks(token);
        setData(refreshedData);
        
        if (refreshedData.pendingCount === 0) {
          setPageState('completed');
        }
      } else {
        // Confirm selected works one by one
        let successCount = 0;
        const newConfirmed = new Set(Array.from(confirmedWorks));
        
        for (const workId of Array.from(selectedWorks)) {
          try {
            await confirmWork(token, {
              workCollaboratorId: workId,
              typedName: data.email,
              consent
            });
            newConfirmed.add(workId);
            successCount++;
          } catch (err) {
            console.error(`Error confirming work ${workId}:`, err);
          }
        }
        
        setConfirmedWorks(newConfirmed);
        setSelectedWorks(new Set());
        
        showToast(`Successfully confirmed ${successCount} split${successCount > 1 ? 's' : ''}!`, 'success');
        
        // Refresh data
        const refreshedData = await getPendingWorks(token);
        setData(refreshedData);
        
        if (refreshedData.pendingCount === 0) {
          setPageState('completed');
        }
      }
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to confirm splits', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRejectWork = async (workId: string) => {
    if (!token) return;

    setIsSubmitting(true);

    try {
      await rejectWork(token, {
        workCollaboratorId: workId,
        reason: rejectReason.trim() || undefined
      });

      showToast('Split rejected', 'success');
      
      // Update local state
      const newRejected = new Set(rejectedWorks);
      newRejected.add(workId);
      setRejectedWorks(newRejected);
      
      // Remove from selected if it was selected
      const newSelected = new Set(selectedWorks);
      newSelected.delete(workId);
      setSelectedWorks(newSelected);
      
      // Reset reject modal state
      setRejectingWorkId(null);
      setRejectReason('');
      
      // Refresh data
      const refreshedData = await getPendingWorks(token);
      setData(refreshedData);
      
      if (refreshedData.pendingCount === 0) {
        setPageState('completed');
      }
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to reject split', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-secondary flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-pink-400"></div>
          <p className="mt-4 text-purple-200">Loading your pending splits...</p>
        </div>
      </div>
    );
  }

  // Invalid/expired link
  if (pageState === 'invalid') {
    return (
      <div className="min-h-screen bg-gradient-secondary flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-surface-secondary rounded-xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-content mb-2">Invalid or Expired Link</h1>
          <p className="text-content-secondary mb-6">{error}</p>
          <a 
            href="/"
            className="btn-primary inline-block"
          >
            Go to Cotune
          </a>
        </div>
        {ToastComponent}
      </div>
    );
  }

  // Error state
  if (pageState === 'error') {
    return (
      <div className="min-h-screen bg-gradient-secondary flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-surface-secondary rounded-xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-content mb-2">Something went wrong</h1>
          <p className="text-content-secondary mb-6">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="btn-primary"
          >
            Try Again
          </button>
        </div>
        {ToastComponent}
      </div>
    );
  }

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!data?.email) return;

    if (signupPassword !== signupConfirmPassword) {
      showToast('Passwords do not match', 'error');
      return;
    }

    if (signupPassword.length < 6) {
      showToast('Password must be at least 6 characters', 'error');
      return;
    }

    setIsSigningUp(true);
    try {
      const response = await authRegister({
        name: signupName.trim(),
        email: data.email,
        password: signupPassword
      });
      
      // Log them in with the returned token
      localStorage.setItem('auth_token', response.token);
      
      showToast('Account created! Redirecting to your splits...', 'success');
      
      // Give a moment for the toast to show, then redirect
      setTimeout(() => {
        navigate('/verify-email');
      }, 1500);
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to create account', 'error');
    } finally {
      setIsSigningUp(false);
    }
  };

  // All completed state
  if (pageState === 'completed') {
    return (
      <div className="min-h-screen bg-gradient-secondary flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-surface-secondary rounded-xl shadow-xl p-8 text-center">
          {/* Logo */}
          <div className="mb-6">
            <img src="/fulllogo.png" alt="Cotune" className="h-10 w-auto mx-auto mb-4" />
          </div>
          
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-content mb-2">All Done!</h1>
          <p className="text-content-secondary mb-6">
            You've confirmed all your pending split agreements. Thank you!
          </p>
          
          {/* Show appropriate next steps based on whether user has an account */}
          {data?.userExists ? (
            <div className="border-t border-[#3F3F46] pt-6 mt-6">
              <p className="text-content-secondary mb-4">
                You already have a Cotune account! Log in to view and manage all your splits.
              </p>
              <button
                onClick={() => navigate('/login')}
                className="w-full px-4 py-3 bg-[#14B8A6] hover:bg-[#0D9488] text-white font-medium rounded-lg transition-colors"
              >
                Go to Login
              </button>
            </div>
          ) : !showSignupForm ? (
            <div className="border-t border-[#3F3F46] pt-6 mt-6">
              <p className="text-content-secondary mb-4">
                Want to view and manage all your splits in one place?
              </p>
              <button
                onClick={() => {
                  setShowSignupForm(true);
                }}
                className="w-full px-4 py-3 bg-[#14B8A6] hover:bg-[#0D9488] text-white font-medium rounded-lg transition-colors"
              >
                Create a Free Account
              </button>
              <p className="text-sm text-content-muted mt-4">
                Or close this page. You'll receive an email if new splits are added.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSignup} className="text-left border-t border-[#3F3F46] pt-6 mt-6">
              <h3 className="text-lg font-semibold text-content mb-4 text-center">Create Your Account</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-content-secondary mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={signupName}
                    onChange={(e) => setSignupName(e.target.value)}
                    className="w-full px-3 py-2 bg-[#18181B] border border-[#3F3F46] rounded-lg text-white placeholder-[#6B7280] focus:outline-none focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent"
                    placeholder="Enter your full name"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-content-secondary mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={data?.email || ''}
                    disabled
                    className="w-full px-3 py-2 bg-[#27272A] border border-[#3F3F46] rounded-lg text-[#9CA3AF] cursor-not-allowed"
                  />
                  <p className="text-xs text-content-muted mt-1">
                    This is the email associated with your splits
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-content-secondary mb-2">
                    Password
                  </label>
                  <input
                    type="password"
                    value={signupPassword}
                    onChange={(e) => setSignupPassword(e.target.value)}
                    className="w-full px-3 py-2 bg-[#18181B] border border-[#3F3F46] rounded-lg text-white placeholder-[#6B7280] focus:outline-none focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent"
                    placeholder="Min 6 characters"
                    minLength={6}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-content-secondary mb-2">
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    value={signupConfirmPassword}
                    onChange={(e) => setSignupConfirmPassword(e.target.value)}
                    className="w-full px-3 py-2 bg-[#18181B] border border-[#3F3F46] rounded-lg text-white placeholder-[#6B7280] focus:outline-none focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent"
                    placeholder="Confirm your password"
                    minLength={6}
                    required
                  />
                  {signupConfirmPassword && signupPassword !== signupConfirmPassword && (
                    <p className="text-red-400 text-sm mt-1">Passwords do not match</p>
                  )}
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowSignupForm(false)}
                  className="flex-1 px-4 py-2 border border-[#3F3F46] text-content-secondary rounded-lg hover:bg-[#3F3F46] transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSigningUp || !signupName.trim() || !signupPassword || signupPassword !== signupConfirmPassword}
                  className="flex-1 px-4 py-2 bg-[#14B8A6] hover:bg-[#0D9488] disabled:bg-[#3F3F46] disabled:text-[#6B7280] text-white font-medium rounded-lg transition-colors"
                >
                  {isSigningUp ? 'Creating...' : 'Create Account'}
                </button>
              </div>
            </form>
          )}
        </div>
        {ToastComponent}
      </div>
    );
  }

  // Ready state - show pending works
  const pendingWorks = data?.works.filter(w => w.status === 'pending' && !confirmedWorks.has(w.id) && !rejectedWorks.has(w.id)) || [];
  const alreadyConfirmedWorks = data?.works.filter(w => w.status === 'confirmed' || confirmedWorks.has(w.id)) || [];
  const alreadyRejectedWorks = data?.works.filter(w => w.status === 'rejected' || rejectedWorks.has(w.id)) || [];

  return (
    <div className="min-h-screen bg-gradient-secondary py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img src="/fulllogo.png" alt="Cotune" className="h-8 w-auto mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-content mb-2">Your Pending Splits</h1>
          <p className="text-content-secondary">
            {data?.inviterName} has invited you to confirm your split agreements
          </p>
          <p className="text-sm text-content-muted mt-1">
            Viewing as: {data?.email}
          </p>
        </div>

        {/* Pending Works List */}
        {pendingWorks.length > 0 && (
          <div className="bg-surface-secondary rounded-xl shadow-xl p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-content">Pending Confirmation</h2>
              <div className="flex gap-2">
                <button 
                  onClick={selectAllPending}
                  className="text-sm text-accent hover:text-accent-hover"
                >
                  Select All
                </button>
                <span className="text-content-muted">|</span>
                <button 
                  onClick={deselectAll}
                  className="text-sm text-content-secondary hover:text-content"
                >
                  Deselect All
                </button>
              </div>
            </div>

            <div className="space-y-3">
              {pendingWorks.map((work) => (
                <WorkCard 
                  key={work.id} 
                  work={work} 
                  isSelected={selectedWorks.has(work.id)}
                  onToggle={() => toggleWorkSelection(work.id)}
                  onReject={() => setRejectingWorkId(work.id)}
                  isConfirmed={false}
                  isRejected={false}
                />
              ))}
            </div>

            {/* Confirmation Form */}
            <form onSubmit={handleConfirmSelected} className="mt-6 pt-6 border-t border-border-subtle">
              <div className="flex items-start mb-4">
                <input
                  type="checkbox"
                  id="consent"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-1 h-4 w-4 text-accent focus:ring-accent border-border-subtle rounded bg-surface-tertiary"
                  required
                  disabled={isSubmitting}
                />
                <label htmlFor="consent" className="ml-2 text-sm text-content-secondary">
                  I confirm that the information in the selected splits is accurate.
                </label>
              </div>

              <button
                type="submit"
                disabled={!consent || selectedWorks.size === 0 || isSubmitting}
                className="btn-primary w-full py-3"
              >
                {isSubmitting 
                  ? 'Confirming...' 
                  : `Confirm ${selectedWorks.size} Selected Split${selectedWorks.size !== 1 ? 's' : ''}`}
              </button>
            </form>
          </div>
        )}

        {/* Already Confirmed Works */}
        {alreadyConfirmedWorks.length > 0 && (
          <div className="bg-surface-secondary rounded-xl shadow-xl p-6">
            <h2 className="text-xl font-semibold text-content mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Already Confirmed
            </h2>
            <div className="space-y-3">
              {alreadyConfirmedWorks.map((work) => (
                <WorkCard 
                  key={work.id} 
                  work={work} 
                  isSelected={false}
                  onToggle={() => {}}
                  onReject={() => {}}
                  isConfirmed={true}
                  isRejected={false}
                />
              ))}
            </div>
          </div>
        )}

        {/* Already Rejected Works */}
        {alreadyRejectedWorks.length > 0 && (
          <div className="bg-surface-secondary rounded-xl shadow-xl p-6 mb-6">
            <h2 className="text-xl font-semibold text-content mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Rejected
            </h2>
            <div className="space-y-3">
              {alreadyRejectedWorks.map((work) => (
                <WorkCard 
                  key={work.id} 
                  work={work} 
                  isSelected={false}
                  onToggle={() => {}}
                  onReject={() => {}}
                  isConfirmed={false}
                  isRejected={true}
                />
              ))}
            </div>
          </div>
        )}

        {/* No pending works message */}
        {pendingWorks.length === 0 && alreadyConfirmedWorks.length === 0 && (
          <div className="bg-surface-secondary rounded-xl shadow-xl p-8 text-center">
            <h2 className="text-xl font-bold text-content mb-3">No Pending Splits</h2>
            <p className="text-content-secondary mb-6">
              There are no pending split agreements for this email address.
            </p>
            
            {data?.userExists ? (
              <div className="space-y-3">
                <p className="text-content-secondary text-sm mb-4">
                  You already have a Cotune account. Log in to view all your splits.
                </p>
                <button
                  onClick={() => navigate('/login')}
                  className="w-full px-4 py-3 bg-[#14B8A6] hover:bg-[#0D9488] text-white font-medium rounded-lg transition-colors"
                >
                  Go to Login
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-content-secondary text-sm mb-4">
                  Want to manage your splits? Create an account or sign in.
                </p>
                <button
                  onClick={() => navigate('/register')}
                  className="w-full px-4 py-3 bg-[#14B8A6] hover:bg-[#0D9488] text-white font-medium rounded-lg transition-colors mb-2"
                >
                  Create Account
                </button>
                <button
                  onClick={() => navigate('/login')}
                  className="w-full px-4 py-3 border border-[#3F3F46] text-content hover:bg-[#3F3F46] font-medium rounded-lg transition-colors"
                >
                  Sign In
                </button>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-content-muted">
          <p>
            Questions? Contact{' '}
            <a href="mailto:help@cotune.app" className="text-accent hover:text-accent-hover underline">
              Cotune Support
            </a>
          </p>
          <p className="mt-2">
            Link expires: {data?.expiresAt ? new Date(data.expiresAt).toLocaleDateString() : 'N/A'}
          </p>
        </div>
      </div>

      {/* Reject Confirmation Modal */}
      {rejectingWorkId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-surface-secondary rounded-xl shadow-xl p-6 max-w-md w-full">
            <h3 className="text-xl font-semibold text-content mb-4">Reject Split?</h3>
            <p className="text-content-secondary mb-4">
              Are you sure you want to reject this split agreement? This action cannot be undone.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-content-secondary mb-2">
                Reason (optional):
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="w-full px-3 py-2 bg-surface-tertiary border border-border-subtle rounded-md text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent resize-none"
                placeholder="e.g., Incorrect share percentage, wrong role..."
                rows={3}
                disabled={isSubmitting}
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setRejectingWorkId(null);
                  setRejectReason('');
                }}
                className="flex-1 px-4 py-2 border border-border-subtle rounded-lg text-content hover:bg-surface-tertiary transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={() => handleRejectWork(rejectingWorkId)}
                className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Rejecting...' : 'Reject Split'}
              </button>
            </div>
          </div>
        </div>
      )}

      {ToastComponent}
    </div>
  );
};

// Work Card Component
interface WorkCardProps {
  work: PendingWork;
  isSelected: boolean;
  onToggle: () => void;
  onReject: () => void;
  isConfirmed: boolean;
  isRejected: boolean;
}

const WorkCard: React.FC<WorkCardProps> = ({ work, isSelected, onToggle, onReject, isConfirmed, isRejected }) => {
  return (
    <div 
      className={`p-4 rounded-lg border transition-colors ${
        isConfirmed 
          ? 'bg-green-500/5 border-green-500/20' 
          : isRejected
            ? 'bg-red-500/5 border-red-500/20'
            : isSelected 
              ? 'bg-accent/10 border-accent' 
              : 'bg-surface-tertiary border-border-subtle hover:border-border-strong'
      } ${!isConfirmed && !isRejected ? 'cursor-pointer' : ''}`}
      onClick={!isConfirmed && !isRejected ? onToggle : undefined}
    >
      <div className="flex items-start gap-3">
        {!isConfirmed && !isRejected && (
          <div className="pt-1">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={onToggle}
              onClick={(e) => e.stopPropagation()}
              className="h-4 w-4 text-accent focus:ring-accent border-border-subtle rounded bg-surface-tertiary"
            />
          </div>
        )}
        {isConfirmed && (
          <div className="pt-1">
            <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        )}
        {isRejected && (
          <div className="pt-1">
            <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        )}
        <div className="flex-1">
          <h3 className="font-medium text-content">{work.workTitle}</h3>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-content-secondary">
            <span>Role: <span className="capitalize">{work.role === 'both' ? 'Composer & Author' : work.role}</span></span>
            <span>Share: <span className="font-medium text-accent">{work.share}%</span></span>
          </div>
        </div>
        {!isConfirmed && !isRejected && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onReject();
            }}
            className="text-sm text-red-400 hover:text-red-300 px-2 py-1 rounded hover:bg-red-500/10 transition-colors"
            title="Reject this split"
          >
            Reject
          </button>
        )}
      </div>
    </div>
  );
};

export default PendingWorksView;
